import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, KeyRound, Database, Eye, EyeOff, ShieldCheck, ChevronLeft, Mail, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { SEO_IMAGES } from '../utils/seo';

/**
 * CompliancePage — LGPD / privacidade / segurança de dados.
 * Inspirada na estrutura de páginas equivalentes do mercado (Doctoralia GDPR),
 * adaptada para a realidade brasileira (LGPD — Lei 13.709/2018).
 */
export default function CompliancePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Segurança e Privacidade | BuscaDoutor';
  }, []);

  const principles = [
    {
      icon: Lock,
      title: 'Criptografia ponta-a-ponta',
      body: 'Dados em trânsito e em repouso são protegidos por criptografia AES-256, padrão bancário. Senhas usam hash com bcrypt + salt único por usuário.',
    },
    {
      icon: ShieldCheck,
      title: 'Conformidade com LGPD',
      body: 'Aderente à Lei 13.709/2018. Você tem direito de acessar, corrigir, exportar e excluir seus dados a qualquer momento — direta e gratuitamente.',
    },
    {
      icon: EyeOff,
      title: 'Nunca vendemos dados',
      body: 'Seus dados pessoais e os dos seus pacientes nunca são vendidos, alugados ou compartilhados com terceiros para fins comerciais.',
    },
    {
      icon: Database,
      title: 'Bases isoladas',
      body: 'Dados de pacientes e dados de profissionais ficam em coleções separadas. Quem é PJ acessa só sua própria agenda — nunca a de outras clínicas.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="compliance-page">
      <SEOHead
        title="Segurança e Privacidade | BuscaDoutor"
        description="Conheça os princípios de segurança, privacidade, LGPD e proteção de dados aplicados à plataforma BuscaDoutor."
        canonicalUrl="/seguranca"
        imageUrl={SEO_IMAGES.security}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F4FAF8] via-white to-[#F4FAF8] py-16 md:py-24">
        <div className="container-app">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#59706A] hover:text-primary mb-8 text-sm transition-colors"
            data-testid="compliance-back-link"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar
          </Link>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 rounded-full mb-6">
              <Shield className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Segurança</span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2421] leading-[1.05] tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Seus dados e os dados dos seus pacientes estão{' '}
              <span className="text-primary">seguros</span> no BuscaDoutor.
            </h1>

            <ul className="space-y-3 text-lg text-[#59706A] mt-8">
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary mt-1 shrink-0" />
                Todos os dados que você inclui pertencem exclusivamente a você
              </li>
              <li className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary mt-1 shrink-0" />
                Sua informação é criptografada e protegida em padrão bancário
              </li>
              <li className="flex items-start gap-3">
                <EyeOff className="w-5 h-5 text-primary mt-1 shrink-0" />
                O BuscaDoutor nunca vende ou compartilha seus dados
              </li>
            </ul>
          </div>
        </div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Ownership */}
      <section className="py-16 md:py-24" data-testid="compliance-ownership">
        <div className="container-app grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">LGPD em prática</span>
            <h2
              className="text-3xl md:text-4xl font-light text-[#1A2421] mt-3 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Você é o <span className="font-medium">único proprietário</span> dos seus dados.
            </h2>
            <p className="text-[#59706A] mt-5 leading-relaxed">
              A Lei Geral de Proteção de Dados (Lei 13.709/2018) garante a você o controle sobre as
              suas informações pessoais. O BuscaDoutor segue a legislação à risca: você decide o
              que entra, o que sai e quem pode ver.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-border/40 p-7 shadow-sm">
            <h3 className="font-semibold text-[#1A2421] mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Os direitos garantidos a você:
            </h3>
            <ul className="space-y-4 text-sm text-[#59706A]">
              <li className="flex items-start gap-3">
                <KeyRound className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-[#1A2421]">Acesso:</strong> consultar, a qualquer momento, todos os dados que mantemos sobre você.</span>
              </li>
              <li className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-[#1A2421]">Portabilidade:</strong> exportar suas informações em formato aberto (JSON / CSV).</span>
              </li>
              <li className="flex items-start gap-3">
                <Eye className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-[#1A2421]">Correção:</strong> atualizar dados incorretos ou desatualizados.</span>
              </li>
              <li className="flex items-start gap-3">
                <EyeOff className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-[#1A2421]">Exclusão:</strong> apagar definitivamente sua conta e dados associados (direito ao esquecimento).</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Patients data */}
      <section className="py-16 md:py-24 bg-[#F4FAF8]" data-testid="compliance-patients">
        <div className="container-app">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Dados dos pacientes</span>
            <h2
              className="text-3xl md:text-4xl font-light text-[#1A2421] mt-3 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Os pacientes que chegam pelo BuscaDoutor{' '}
              <span className="font-medium">também são seus.</span>
            </h2>
            <p className="text-[#59706A] mt-5 leading-relaxed">
              As bases de dados do BuscaDoutor e da agenda de cada profissional ficam totalmente
              separadas — justamente para que nunca se misturem. As mesmas regras de segurança
              valem para os dois lados.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-border/40">
              <h3 className="font-semibold text-[#1A2421] mb-4 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Os dados dos seus pacientes
              </h3>
              <ul className="space-y-3 text-sm text-[#59706A]">
                <li>• Nunca são vendidos ou compartilhados com terceiros.</li>
                <li>• O BuscaDoutor nunca envia promoções aos seus pacientes — apenas você pode, se quiser.</li>
                <li>• Outros profissionais ou clínicas nunca veem seus pacientes (a menos que você mesmo dê esse acesso à equipe).</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-border/40">
              <h3 className="font-semibold text-[#1A2421] mb-4 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Pacientes da plataforma BuscaDoutor
              </h3>
              <ul className="space-y-3 text-sm text-[#59706A]">
                <li>• Não são vendidos ou compartilhados com terceiros.</li>
                <li>• Recebem comunicações úteis de saúde apenas se assinarem — e podem cancelar a qualquer momento.</li>
                <li>• Você só vê os dados de um paciente quando ele agenda com você diretamente.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 md:py-24" data-testid="compliance-principles">
        <div className="container-app">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Como protegemos</span>
            <h2
              className="text-3xl md:text-4xl font-light text-[#1A2421] mt-3 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Quatro pilares de{' '}
              <span className="font-medium">segurança e conformidade.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {principles.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-white rounded-2xl p-7 border border-border/40 hover:shadow-[0_8px_30px_rgba(28,78,66,0.08)] transition-shadow"
                  data-testid={`principle-${p.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-[#1A2421] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#59706A] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 md:py-24 bg-[#1C4E42] text-white relative overflow-hidden" data-testid="compliance-contact">
        <div className="container-app relative z-10 max-w-3xl">
          <h2
            className="text-3xl md:text-4xl font-light leading-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Tem dúvidas sobre como tratamos seus dados?
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
            Entre em contato com nosso encarregado de proteção de dados (DPO). Respondemos em até
            72 horas conforme determina a LGPD.
          </p>
          <a
            href="mailto:support@buscadoutor.com.br?subject=LGPD%20-%20Solicita%C3%A7%C3%A3o%20de%20titular%20de%20dados"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-[#1A2421] font-semibold transition-colors"
            data-testid="compliance-contact-email"
          >
            <Mail className="w-5 h-5" />
            support@buscadoutor.com.br
          </a>
        </div>
        <div className="absolute -bottom-32 -right-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      </section>

      <Footer />
    </div>
  );
}
